// The quote list. THE FRONT DOOR.
//
// ⚠️ THIS SCREEN READS THE DATABASE. It used to render mocks/quotes.ts — 25
// invented quotes with invented values, margins and validity dates — which
// meant the first screen of the app was fiction, and every figure on it was
// a number nobody computed. It now reads api.listSavedQuotes(), the same
// endpoint /saved-quotes used, and there is only one quote list again.
//
// ⚠️ THREE COLUMNS WENT WITH THE CUTOVER, EACH FOR WANT OF A SOURCE, and
// none may come back on the strength of "the table looks sparse":
//
//   Ship To      read q.customer.shipping.name off the mock object. The
//                summary endpoint serializes partner_name only; the shipping
//                child is on the DETAIL read. A dash in every row would say
//                "no ship-to recorded" about quotes that have one.
//   Margin %     NOTHING computes a quote-level margin. The mock number was
//                a literal in the array. Margin is per LINE, from the engine,
//                and aggregating it here would be pricing arithmetic in a
//                list view — see CLAUDE.md's "Pricing calculations" hard rule.
//   Valid Until  anixify.quote HAS NO VALIDITY FIELD. The owner confirmed
//                quote validity is real business (see HANDOFF, 2026-09-07)
//                and it is UNBUILT. Showing a date for it would invent the
//                one thing a customer reads a quote expiry off.
//
// The mock quotes still exist in mocks/quotes.ts and are still reachable at
// /quotes/:quoteNo by typing the URL — that is deliberate and is what the C1
// / C1a example-quote guards are for. Nothing in the UI links to them any
// more.

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, AlertCircle, FileText, Search } from "lucide-react";
import { getFollowUp, listQuotesPage } from "@/services/api";
import { MyNextActions } from "@/components/MyNextActions";
import type { QuoteListPage, SavedQuoteSummary } from "@/services/api";
import { LinePager } from "@/components/LinePager";
import {
  QUOTES_PAGE_SIZES, STATUS_KEYS, STATUS_LABELS, activeChips, clearAll, isDueOrder, isFiltered, parseView, readQuotesPageSize,
  removeChip, storeQuotesPageSize, toApiParams, toggleSort, viewToSearch, withFilter,
  type QuotesPageSize, type QuotesView, type SortKey, type StatusKey,
} from "@/lib/quotesListView";
import { useAdminView, useCanSeeFollowUpOrFalse } from "@/auth/AuthContext";
import { NewQuoteModal } from "@/components/NewQuoteModal";
import { Pill, QUOTE_STATUS_LABEL, BADGE_LABEL, quoteStatusTone } from "@/components/OemConfirmationsPanel";
import { formatDate, formatRupees } from "@/lib/format";
import {
  colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, motion,
} from "@/styles/tokens";

const HEADER_STYLE: React.CSSProperties = {
  height: heights.control,
  fontSize: typography.fontSize.sm,
  color: semanticColors.textSecondary,
  textTransform: "uppercase",
  letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium,
  textAlign: "left",
  padding: `0 ${spacing[12]}px`,
  borderBottom: `1px solid ${semanticColors.border}`,
  backgroundColor: semanticColors.panelBg,
  whiteSpace: "nowrap",
};

const CELL_STYLE: React.CSSProperties = {
  padding: `0 ${spacing[12]}px`,
  fontSize: typography.fontSize.sm,
  color: semanticColors.textPrimary,
  borderBottom: `1px solid ${semanticColors.border}`,
  whiteSpace: "nowrap",
};

const NEW_QUOTE_STYLE: React.CSSProperties = {
  height: heights.control,
  padding: `0 ${spacing[14]}px`,
  backgroundColor: colors.indigo600,
  color: semanticColors.onAccent,
  border: "none",
  borderRadius: radius.base,
  fontSize: typography.fontSize.base,
  fontWeight: typography.fontWeight.medium,
  cursor: "pointer",
};

// Odoo's own state values. Kept local rather than reusing components/
// StatusPill, which is typed to the mock quotes and their
// Draft/Sent/Won/Lost/Expired strings — the same local-pill convention every
// other table in this app already follows.
const STATE_LABELS: Record<string, string> = {
  draft: "Draft",
  // A FINALISED quote whose state is still "draft" -- which is every one,
  // since the addon never writes state. See the call site.
  finalised: "Finalised",
  sent: "Sent",
  won: "Won",
  lost: "Lost",
  // quote-won-lost: the outcome the backend reports on a finalised quote.
  open: "Open",
  expired: "Expired",
  // quote-revisions: replaced by its finalised revision.
  superseded: "Superseded",
};

function StatePill({ state }: { state: string }) {
  const won = state === "won";
  const lost = state === "lost";
  const expired = state === "expired";
  if (expired) {
    return (
      <span
        data-testid="quote-state-expired"
        style={{
          display: "inline-flex", alignItems: "center", height: heights.pill,
          padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
          fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
          color: semanticColors.warningTextOnTint, backgroundColor: semanticColors.warningBg,
          border: `1px solid ${semanticColors.warningBorder}`,
        }}
      >
        Expired
      </span>
    );
  }
  return (
    <span
      data-testid={`quote-state-${state}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: heights.pill,
        padding: `0 ${spacing[8]}px`,
        borderRadius: radius.base,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.medium,
        color: won ? colors.green600 : lost ? colors.red600 : semanticColors.textSecondary,
        backgroundColor: won ? colors.green50 : lost ? colors.red50 : semanticColors.panelBg,
        border: `1px solid ${won ? colors.green100 : lost ? colors.red100 : semanticColors.border}`,
      }}
    >
      {STATE_LABELS[state] ?? state}
    </span>
  );
}

/** An unfinished working set, not a quote that has been issued.
 *
 *  ⚠️ ITS OWN PILL RATHER THAN A STATE VALUE, because it answers a different
 *  question from StatePill -- see the call site. The amber "in-progress" tone:
 *  a draft is ordinary and unfinished, not an error and not a success.
 *
 *  Built from the SEMANTIC warning tokens rather than raw colors.amber*, which
 *  is where tokens.ts says a value playing a standard role belongs. StatePill
 *  beside it still reaches for the raw scale; that is pre-existing and is not
 *  this slice's to tidy. */
/** margin-approval: a draft awaiting an Admin's approval. Same amber tone as
 *  a draft -- it IS a draft, locked until it is approved or rejected. */
function AwaitingApprovalPill() {
  return (
    <span
      data-testid="quotes-awaiting-pill"
      style={{
        display: "inline-flex", alignItems: "center", height: heights.pill,
        padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        color: semanticColors.warningTextOnTint, backgroundColor: semanticColors.warningBg,
        border: `1px solid ${semanticColors.warningBorder}`,
      }}
    >
      Awaiting approval
    </span>
  );
}

function DraftPill() {
  return (
    <span
      data-testid="quotes-draft-pill"
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: heights.pill,
        padding: `0 ${spacing[8]}px`,
        borderRadius: radius.base,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.medium,
        color: semanticColors.warningTextOnTint,
        backgroundColor: semanticColors.warningBg,
        border: `1px solid ${semanticColors.warningBorder}`,
      }}
    >
      Draft
    </span>
  );
}

type Status = "loading" | "ok" | "error";

// A row's status as the pills read it. The server sends `status_key`; a row
// from a backend predating it (or a test fixture) falls back to the old fields.
function rowStatus(q: SavedQuoteSummary): string {
  if (q.is_draft) return "draft";
  return q.outcome_status ?? (q.state === "draft" ? "finalised" : q.state);
}

/** follow-up-list: "due in 3 days" / "due today" / "2 days overdue", from
 *  the server's own day count. */
function dueText(daysLeft: number): string {
  if (daysLeft === 0) return "due today";
  if (daysLeft > 0) return `due in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`;
  const n = -daysLeft;
  return `${n} day${n === 1 ? "" : "s"} overdue`;
}

const CONTROL: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[8]}px`,
  backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, boxSizing: "border-box",
};

/** One sortable column header. A button, so it works from the keyboard; the
 *  th carries aria-sort. */
function SortHeader({ label, sortKey, view, onSort, align }: {
  label: string; sortKey: SortKey; view: QuotesView; onSort: (k: SortKey) => void; align?: "right";
}) {
  // The follow-up list in its own (due) order has no active column.
  const active = view.sort === sortKey && !isDueOrder(view);
  return (
    <th style={{ ...HEADER_STYLE, textAlign: align ?? "left" }}
        aria-sort={active ? (view.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" data-testid={`quotes-sort-${sortKey}`} data-active={active ? "true" : "false"}
              onClick={() => onSort(sortKey)}
              style={{
                all: "unset", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: spacing[4],
                textTransform: "inherit", letterSpacing: "inherit", fontWeight: "inherit",
                color: active ? semanticColors.textPrimary : "inherit",
              }}>
        {label}
        {active && <span aria-hidden="true">{view.dir === "asc" ? "▲" : "▼"}</span>}
      </button>
    </th>
  );
}

export default function QuotesPage() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // THE VIEW LIVES IN THE URL (quotes-list-usability): page, sort, search and
  // filters, so a link or a reload comes back to the same list. Written with
  // history.replaceState rather than the router's navigate on purpose: Layout
  // keys this page on location.search (a top-bar search lands here with
  // ?q=...), so a router navigation per click would REMOUNT the page on every
  // filter change. The page owns the URL it shows.
  const [view, setViewState] = useState<QuotesView>(() => parseView(window.location.search));
  const setView = useCallback((next: QuotesView) => {
    setViewState(next);
    try {
      window.history.replaceState(window.history.state, "", window.location.pathname + viewToSearch(next));
    } catch { /* a sandboxed frame: the view just is not in the URL */ }
  }, []);
  const [pageSize, setPageSizeState] = useState<QuotesPageSize>(() => readQuotesPageSize());
  const setPageSize = (size: QuotesPageSize) => {
    storeQuotesPageSize(size);
    setPageSizeState(size);
    setView({ ...view, page: 1 });
  };
  // The search box is typed into freely and sent after a pause.
  const [searchText, setSearchText] = useState(view.q);

  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState("");
  const [data, setData] = useState<QuoteListPage | null>(null);
  const [hoverRow, setHoverRow] = useState<number | null>(null);
  // Order Management O2: a client-side narrowing of the loaded page by the
  // derived OEM status. Rows the caller may not see confirmations for (oem
  // null) match only "All".
  const [oemFilter, setOemFilter] = useState("");
  const [tick, setTick] = useState(0);
  const { isAdmin, uid } = useAdminView();
  // follow-up-list: the COUNT on the button, its own read (the list itself is
  // the follow_up filter on the one paged read). A failure here says so on the
  // button and never empties the list.
  const canFollowUp = useCanSeeFollowUpOrFalse();
  const [followUpCount, setFollowUpCount] = useState<number | null>(null);
  const [followUpError, setFollowUpError] = useState("");

  useEffect(() => {
    if (!canFollowUp) return;
    let live = true;
    void getFollowUp().then((r) => {
      if (!live) return;
      setFollowUpCount(r.ok ? (r.quotes ?? []).length : null);
      setFollowUpError(r.ok ? "" : r.error ?? "The follow-up list could not be read.");
    });
    return () => { live = false; };
  }, [canFollowUp, tick]);

  // ONE read, last response wins. Rows already on screen stay (dimmed by the
  // Refresh button's spinner) while a new page loads -- paging should not
  // flash the table away -- but ANY failure clears them: rows under a failed
  // refresh imply they are current, and a partial list is the worst outcome.
  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setStatus("loading");
    setError("");
    const result = await listQuotesPage(toApiParams(view, pageSize));
    if (mine !== seq.current) return;
    if (!result.ok || !result.page) {
      setData(null);
      setError(result.error ?? "Backend reported an unknown failure.");
      setStatus("error");
      return;
    }
    // The server clamps a page past the end; follow it so the URL is true.
    if (result.page.page !== view.page) setView({ ...view, page: result.page.page });
    setData(result.page);
    setStatus("ok");
  }, [view, pageSize, setView]);

  useEffect(() => { void load(); }, [load, tick]);

  // The debounced search -> the view (and back to page 1).
  useEffect(() => {
    if (searchText.trim() === view.q) return;
    const t = setTimeout(() => setView(withFilter(view, { q: searchText.trim().slice(0, 80) })), 300);
    return () => clearTimeout(t);
  }, [searchText, view, setView]);
  // A chip or Clear all changed the search: the box follows.
  useEffect(() => { setSearchText(view.q); }, [view.q]);

  const allQuotes = data?.quotes ?? [];
  const quotes = oemFilter
    ? allQuotes.filter((q) => q.oem && (oemFilter === "price_differs"
      ? q.oem.badges.includes("price_differs") : q.oem.status === oemFilter))
    : allQuotes;
  const total = data?.total ?? 0;
  const filtered = isFiltered(view);
  const customerName = (id: number) => data?.customers.find((c) => c.id === id)?.name;
  const ownerName = (id: number) => data?.owners.find((o) => o.uid === id)?.name;
  const chips = activeChips(view, { customer: customerName, owner: ownerName });
  const onSort = (k: SortKey) => setView(toggleSort(view, k));

  return (
    <div data-testid="quotes-page" className="flex flex-col" style={{ padding: spacing[24] }}>
      <div className="flex items-baseline justify-between"
           style={{ marginBottom: spacing[16], flexWrap: "wrap", gap: spacing[12] }}>
        <div style={{ minWidth: 0 }}>
          <h1
            data-testid="quotes-title"
            style={{
              fontSize: typography.fontSize.xxl,
              fontWeight: typography.fontWeight.semibold,
              color: semanticColors.textPrimary,
              letterSpacing: typography.letterSpacing.tight,
            }}
          >
            Quotes
          </h1>
          <div
            data-testid="quotes-subtitle"
            style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4] }}
          >
            All quotes you can see, newest first.
          </div>
        </div>
        {/* app review P2-3: no label breaks inside its button ("New / Quote");
            the controls wrap as whole items instead. */}
        <div className="flex items-center" style={{
          gap: spacing[12], flexWrap: "wrap", justifyContent: "flex-end", whiteSpace: "nowrap",
        }}>
          {/* margin-approval: an Admin sees how many quotes await approval,
              and can filter to them. */}
          {data && isAdmin && (
            <button
              data-testid="quotes-awaiting-filter"
              data-active={view.status === "awaiting" ? "true" : "false"}
              onClick={() => setView(withFilter(view, { status: view.status === "awaiting" ? "" : "awaiting" }))}
              style={{
                height: heights.control, padding: `0 ${spacing[12]}px`,
                backgroundColor: view.status === "awaiting" ? semanticColors.warningBg : semanticColors.surface,
                color: view.status === "awaiting" ? semanticColors.warningTextOnTint : semanticColors.textSecondary,
                border: `1px solid ${view.status === "awaiting" ? semanticColors.warningBorder : semanticColors.border}`,
                borderRadius: radius.base, fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium, cursor: "pointer",
              }}
            >
              Awaiting approval{" "}
              <span data-testid="quotes-awaiting-count" className="tabular-nums">{data.awaitingCount}</span>
            </button>
          )}
          {data && canFollowUp && (
            <button
              data-testid="quotes-follow-up-filter"
              data-active={view.followUp ? "true" : "false"}
              onClick={() => setView(withFilter(view, { followUp: !view.followUp }))}
              title="Finalised Open quotes whose Valid Until is within 7 days or already past, soonest first."
              style={{
                height: heights.control, padding: `0 ${spacing[12]}px`,
                backgroundColor: view.followUp ? semanticColors.warningBg : semanticColors.surface,
                color: view.followUp ? semanticColors.warningTextOnTint : semanticColors.textSecondary,
                border: `1px solid ${view.followUp ? semanticColors.warningBorder : semanticColors.border}`,
                borderRadius: radius.base, fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium, cursor: "pointer",
              }}
            >
              Follow up{" "}
              <span data-testid="quotes-follow-up-count" className="tabular-nums">
                {followUpCount ?? "—"}
              </span>
            </button>
          )}
          {data && (
            <div style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary }}>
              <span data-testid="quotes-count" className="tabular-nums">{total}</span>{" "}
              {total === 1 ? "quote" : "quotes"}
            </div>
          )}
          <button
            data-testid="quotes-refresh"
            disabled={status === "loading"}
            onClick={() => setTick((t) => t + 1)}
            style={{
              height: heights.control,
              padding: `0 ${spacing[12]}px`,
              backgroundColor: semanticColors.surface,
              color: semanticColors.textSecondary,
              border: `1px solid ${semanticColors.border}`,
              borderRadius: radius.base,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.medium,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: spacing[6],
              opacity: status === "loading" ? 0.6 : 1,
            }}
          >
            <RefreshCw
              size={iconSize.md}
              strokeWidth={iconStroke.regular}
              className={status === "loading" ? "animate-spin" : undefined}
            />
            {status === "loading" ? "Loading…" : "Refresh"}
          </button>
          <button
            data-testid="quotes-new-quote"
            className="btn-primary"
            onClick={() => setIsModalOpen(true)}
            style={NEW_QUOTE_STYLE}
          >
            New Quote
          </button>
        </div>
      </div>

      {/* ux-guided-deal: what needs me, from two existing reads, on demand. */}
      <MyNextActions isAdmin={isAdmin} uid={uid} onOpenQuote={(qid) => navigate(`/saved-quotes/${qid}`)} />

      {/* THE FILTER BAR (quotes-list-usability): search, status, customer,
          owner (Admin only), date range. Each one is a REQUEST to narrow --
          the server decides scope and refuses what a role may not use. */}
      {(data || filtered) && (
        <div data-testid="quotes-filter-bar" style={{
          display: "flex", alignItems: "center", gap: spacing[8], flexWrap: "wrap", marginBottom: spacing[8],
        }}>
          <div style={{
            ...CONTROL, display: "flex", alignItems: "center", gap: spacing[6], flex: "1 1 220px",
            maxWidth: layout.topBarSearchWidth,
          }}>
            <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
            <input
              data-testid="quotes-search"
              aria-label="Search quotes by number, customer or project"
              placeholder="Search quote no., customer, project"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
                       fontSize: typography.fontSize.base, color: semanticColors.textPrimary }}
            />
          </div>
          <select data-testid="quotes-status-filter" aria-label="Filter by status" value={view.status}
                  onChange={(e) => setView(withFilter(view, { status: e.target.value as StatusKey | "" }))}
                  style={CONTROL}>
            <option value="">All statuses</option>
            {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
          </select>
          <select data-testid="quotes-oem-filter" aria-label="Filter by OEM confirmation"
                  value={oemFilter} onChange={(e) => setOemFilter(e.target.value)} style={CONTROL}>
            <option value="">Any OEM status</option>
            <option value="awaiting_oem">Awaiting OEM confirmation</option>
            <option value="oem_confirmed">OEM confirmed</option>
            <option value="price_differs">OEM price differs</option>
          </select>
          <select data-testid="quotes-customer-filter" aria-label="Filter by customer"
                  value={view.customer === null ? "" : String(view.customer)}
                  onChange={(e) => setView(withFilter(view, { customer: e.target.value ? Number(e.target.value) : null }))}
                  style={{ ...CONTROL, maxWidth: layout.searchMinWidthWide + spacing[40] }}>
            <option value="">All customers</option>
            {view.customer !== null && !customerName(view.customer) && (
              <option value={String(view.customer)}>{`Customer #${view.customer}`}</option>
            )}
            {(data?.customers ?? []).map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </select>
          {isAdmin && (
            <select data-testid="quotes-owner-filter" aria-label="Filter by owner"
                    value={view.owner === null ? "" : String(view.owner)}
                    onChange={(e) => setView(withFilter(view, { owner: e.target.value ? Number(e.target.value) : null }))}
                    style={CONTROL}>
              <option value="">All owners</option>
              {view.owner !== null && !ownerName(view.owner) && (
                <option value={String(view.owner)}>{`Owner #${view.owner}`}</option>
              )}
              {(data?.owners ?? []).map((o) => <option key={o.uid} value={String(o.uid)}>{o.name}</option>)}
            </select>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: spacing[4],
                          fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
            From
            <input data-testid="quotes-date-from" type="date" value={view.from} max={view.to || undefined}
                   onChange={(e) => setView(withFilter(view, { from: e.target.value }))} style={CONTROL} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: spacing[4],
                          fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
            To
            <input data-testid="quotes-date-to" type="date" value={view.to} min={view.from || undefined}
                   onChange={(e) => setView(withFilter(view, { to: e.target.value }))} style={CONTROL} />
          </label>
        </div>
      )}

      {chips.length > 0 && (
        <div data-testid="quotes-chips" style={{
          display: "flex", alignItems: "center", gap: spacing[6], flexWrap: "wrap", marginBottom: spacing[8],
        }}>
          {chips.map((c) => (
            <button key={c.key} type="button" data-testid={`quotes-chip-${c.key}`}
                    onClick={() => setView(removeChip(view, c.key))}
                    title={`Remove: ${c.label}`} aria-label={`Remove filter: ${c.label}`}
                    style={{
                      height: heights.pill + spacing[6], padding: `0 ${spacing[8]}px`, cursor: "pointer",
                      border: `1px solid ${semanticColors.border}`, borderRadius: radius.full,
                      backgroundColor: semanticColors.panelBg, color: semanticColors.textSecondary,
                      fontSize: typography.fontSize.sm, whiteSpace: "nowrap",
                    }}>
              {c.label} <span aria-hidden="true">{"✕"}</span>
            </button>
          ))}
          <button type="button" data-testid="quotes-clear-all" onClick={() => setView(clearAll(view))}
                  style={{ border: "none", background: "transparent", cursor: "pointer",
                           color: colors.indigo600, fontSize: typography.fontSize.sm }}>
            Clear all
          </button>
        </div>
      )}

      {status === "error" && (
        <div
          data-testid="quotes-error"
          className="flex items-start"
          style={{
            padding: `${spacing[12]}px`,
            border: `1px solid ${semanticColors.destructiveBorder}`,
            backgroundColor: semanticColors.destructiveBg,
            borderRadius: radius.base,
            gap: spacing[8],
            fontSize: typography.fontSize.sm,
            color: semanticColors.destructive,
          }}
        >
          <AlertCircle size={iconSize.md} strokeWidth={iconStroke.regular} style={{ flexShrink: 0, marginTop: spacing[2] }} />
          <span>Could not load quotes. {error}</span>
        </div>
      )}

      {status === "loading" && !data && (
        <div
          data-testid="quotes-loading"
          style={{
            padding: spacing[40],
            textAlign: "center",
            fontSize: typography.fontSize.base,
            color: semanticColors.textPlaceholder,
          }}
        >
          Loading quotes…
        </div>
      )}

      {data && total === 0 && !filtered && (
        <div
          data-testid="quotes-empty"
          className="flex flex-col items-center justify-center"
          style={{
            padding: spacing[40],
            gap: spacing[8],
            border: `1px solid ${semanticColors.border}`,
            borderRadius: radius.base,
            backgroundColor: semanticColors.panelBg,
          }}
        >
          <FileText size={iconSize.lg} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
          <div style={{ fontSize: typography.fontSize.base, color: semanticColors.textPrimary, fontWeight: typography.fontWeight.medium }}>
            No quotes yet
          </div>
          <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, textAlign: "center" }}>
            Create one, add materials, and press Save Quote. It will appear here.
          </div>
          {/* AN EMPTY STATE THAT DEAD-ENDS IS THE WORST KIND. The one action
              that gets someone out of it is the same one in the header. */}
          <button
            data-testid="quotes-empty-new-quote"
            className="btn-primary"
            onClick={() => setIsModalOpen(true)}
            style={{ ...NEW_QUOTE_STYLE, marginTop: spacing[8] }}
          >
            New Quote
          </button>
        </div>
      )}

      {data && (total > 0 || filtered) && (
        <div
          data-testid="quotes-panel"
          style={{
            backgroundColor: semanticColors.panelBg,
            border: `1px solid ${semanticColors.border}`,
            borderRadius: radius.base,
            overflow: "hidden",
            opacity: status === "loading" ? 0.6 : 1,
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              data-testid="quotes-table"
              style={{ width: "100%", borderCollapse: "collapse", backgroundColor: semanticColors.surface }}
            >
              <thead>
                <tr>
                  <SortHeader label="Quote No." sortKey="name" view={view} onSort={onSort} />
                  <SortHeader label="End Client" sortKey="customer" view={view} onSort={onSort} />
                  <th style={{ ...HEADER_STYLE, textAlign: "right" }}>Lines</th>
                  <SortHeader label="Value Rs" sortKey="total" view={view} onSort={onSort} align="right" />
                  <SortHeader label="Status" sortKey="status" view={view} onSort={onSort} />
                  <SortHeader label="Owner" sortKey="owner" view={view} onSort={onSort} />
                  <SortHeader label="Created" sortKey="date" view={view} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {quotes.map((q, i) => {
                  const isHover = hoverRow === i;
                  return (
                    <tr
                      key={q.quote_id}
                      data-testid={`quote-row-${q.quote_id}`}
                      onMouseEnter={() => setHoverRow(i)}
                      onMouseLeave={() => setHoverRow(null)}
                      // ⚠️ /saved-quotes/:savedId, NOT /quotes/:name. The route
                      // is what tells a real database record apart from a
                      // shipped example quote sharing its name — see
                      // services/mockQuoteGuard.ts's Fence 1. Keying this on
                      // the name would land a real quote on the mock one.
                      onClick={() => navigate(`/saved-quotes/${q.quote_id}`)}
                      style={{
                        height: heights.row,
                        backgroundColor: isHover ? semanticColors.hoverBg : semanticColors.surface,
                        cursor: "pointer",
                        transition: `background-color ${motion.fast}`,
                      }}
                    >
                      <td style={{ ...CELL_STYLE, fontWeight: typography.fontWeight.medium }}>{q.name}</td>
                      <td style={CELL_STYLE}>
                        {q.partner_name ?? (
                          <span style={{ color: semanticColors.textPlaceholder }}>—</span>
                        )}
                      </td>
                      <td style={{ ...CELL_STYLE, textAlign: "right", fontVariantNumeric: "tabular-nums", color: semanticColors.textSecondary }}>
                        {q.line_count}
                      </td>
                      <td
                        data-testid={`quote-total-${q.quote_id}`}
                        style={{ ...CELL_STYLE, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: typography.fontWeight.medium }}
                      >
                        {/* ⚠️ A QUOTE WITH NO LINES SHOWS "—", NEVER Rs 0.
                            Owner decision, 2026-09-17, and it arrived with
                            the lineless draft: a quote started from a
                            customer PO exists before it has any lines, and
                            `formatRupees(0)` would print a confident Rs 0 --
                            a total somebody could read as "this quote is
                            worth nothing" rather than "nothing has been
                            added yet". Same absent-is-not-zero rule the
                            stock column and the unpriced line already keep.

                            ⚠️ KEYED ON line_count, NOT ON grand_total === 0.
                            A real quote CAN legitimately total zero (a free
                            line is a fact somebody entered), and dashing that
                            would hide a figure that was actually stated. */}
                        {q.line_count === 0 ? (
                          <span style={{ color: semanticColors.textPlaceholder }}>—</span>
                        ) : (
                          formatRupees(q.grand_total)
                        )}
                      </td>
                      <td style={CELL_STYLE}>
                        {/* ⚠️ DRAFT BEATS state, AND IT IS NOT THE SAME FACT.
                            `state` is draft/sent/won/lost -- where the quote
                            is commercially. is_draft is whether it has been
                            FINALISED at all, i.e. whether it has drawn a real
                            sequence number. A row can be state="draft" and
                            finalised, so rendering only StatePill would show
                            two different things under one word.

                            ⚠️ AND A FINALISED ROW NEVER READS "Draft" EITHER.
                            The addon never writes `state`, so every finalised
                            quote arrives state="draft" -- and StatePill used to
                            print exactly that word under a quote that HAS a
                            number. is_draft is already the truth and this row
                            already has it. */}
                        {q.is_draft && q.approval_state === "pending"
                          ? <AwaitingApprovalPill />
                          : q.oem && q.oem.status !== "draft"
                          // Order Management O2: the ONE derived status (D181 #2).
                          ? <Pill testId={`quote-oem-status-${q.quote_id}`} tone={quoteStatusTone(q.oem.status)}>
                              {QUOTE_STATUS_LABEL[q.oem.status]}
                            </Pill>
                          : q.is_draft
                          ? <DraftPill />
                          : <StatePill state={rowStatus(q)} />}
                        {q.oem?.badges.map((b) => (
                          <span key={b} style={{ marginLeft: spacing[4] }}>
                            <Pill testId={`quote-oem-badge-${q.quote_id}-${b}`} tone={b === "oem_expired" ? "red" : "amber"}>
                              {BADGE_LABEL[b]}
                            </Pill>
                          </span>
                        ))}
                        {view.followUp && q.days_left != null && (
                          <span
                            data-testid={`quotes-due-${q.quote_id}`}
                            style={{ marginLeft: spacing[8], fontSize: typography.fontSize.xs, color: semanticColors.textSecondary }}
                          >
                            {dueText(q.days_left)}
                          </span>
                        )}
                      </td>
                      <td style={{ ...CELL_STYLE, color: semanticColors.textSecondary }}>
                        {q.owner_name ? (
                          <span data-testid={`quotes-owner-${q.quote_id}`}>
                            {q.owner_uid === uid ? `${q.owner_name} (you)` : q.owner_name}
                          </span>
                        ) : (
                          <span style={{ color: semanticColors.textPlaceholder }}>—</span>
                        )}
                      </td>
                      <td style={{ ...CELL_STYLE, color: semanticColors.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                        {q.date ? formatDate(q.date) : "—"}
                      </td>
                    </tr>
                  );
                })}
                {quotes.length === 0 && (
                  <tr data-testid="quotes-filter-empty">
                    <td colSpan={7} style={{ ...CELL_STYLE, height: heights.row, color: semanticColors.textSecondary }}>
                      {view.followUp && followUpError
                        ? `The follow-up list could not be read. ${followUpError}`
                        : view.followUp && chips.length === 1
                        ? "Nothing to follow up: no Open quote is due within 7 days or past its Valid Until."
                        : "No quotes match this filter."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <LinePager
              testIdPrefix="quotes-pager"
              noun="Quotes"
              sizes={QUOTES_PAGE_SIZES}
              total={total}
              page={(data?.page ?? 1) - 1}
              pageSize={pageSize}
              onPage={(p) => setView({ ...view, page: p + 1 })}
              onPageSize={(s) => setPageSize(s as QuotesPageSize)}
            />
          )}
        </div>
      )}

      {isModalOpen && <NewQuoteModal onClose={() => setIsModalOpen(false)} />}
    </div>
  );
}
